import { Button, Flex, Input, Select, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { Delete, PlusBlueWithoutBackground } from '@shared/assets/icons/components';
import { Text } from '@shared/ui/antd';
import { CONTACT_FORM_CHOICE_FIELD_TYPES, CONTACT_FORM_FIELD_TYPES } from '../../lib/constants';
import type { ContactFormField, ContactFormFieldType } from '../../lib/types';
import { PresetFormItem } from './form-item';
import styles from './preset-form.module.scss';

export interface FieldDraftErrors {
  alias?: string;
  label?: string;
  options?: string;
}

interface ContactFormFieldDraftProps {
  draft: ContactFormField;
  errors: FieldDraftErrors;
  isCreating: boolean;
  disabled?: boolean;
  onChange: (changes: Partial<ContactFormField>) => void;
  onClearError: (field: keyof FieldDraftErrors) => void;
  onUpdateOption: (index: number, value: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

const isChoiceField = (type: ContactFormFieldType) =>
  CONTACT_FORM_CHOICE_FIELD_TYPES.includes(type);

export const ContactFormFieldDraft = ({
  draft,
  errors,
  isCreating,
  disabled,
  onChange,
  onClearError,
  onUpdateOption,
  onCancel,
  onSave,
}: ContactFormFieldDraftProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'presets.editor.advanced.contactForm.fields',
  });

  return (
    <div className={styles.additionalFieldEditor}>
      <Flex align="center" justify="space-between" gap={12}>
        <Text variant="sm" fontWeight={600}>
          {t(isCreating ? 'newFieldTitle' : 'editFieldTitle')}
        </Text>
        <Text variant="xs" className={styles.mutedText}>
          {t('draftHint')}
        </Text>
      </Flex>

      <div className={styles.fieldsGrid}>
        <PresetFormItem label={t('type')} required>
          <Select
            value={draft.type}
            disabled={disabled}
            options={CONTACT_FORM_FIELD_TYPES.map((type) => ({
              value: type,
              label: t(`types.${type}`),
            }))}
            onChange={(type: ContactFormFieldType) => {
              onChange({
                type,
                options: isChoiceField(type)
                  ? draft.options.length
                    ? draft.options
                    : ['', '']
                  : [],
              });
              onClearError('options');
            }}
          />
        </PresetFormItem>

        <PresetFormItem label={t('label')} error={errors.label} required>
          <Input
            value={draft.label}
            maxLength={120}
            disabled={disabled}
            placeholder={t('labelPlaceholder')}
            onChange={(event) => {
              onChange({ label: event.target.value });
              onClearError('label');
            }}
          />
        </PresetFormItem>

        <PresetFormItem label={t('alias')} error={errors.alias} fullWidth required>
          <Input
            value={draft.alias}
            maxLength={128}
            disabled={disabled}
            placeholder={t('aliasPlaceholder')}
            addonBefore="alias"
            onChange={(event) => {
              onChange({ alias: event.target.value });
              onClearError('alias');
            }}
          />
          <Text variant="xs" className={styles.mutedText}>
            {t('aliasHint')}
          </Text>
        </PresetFormItem>

        <PresetFormItem label={t('placeholder')} fullWidth>
          <Input
            value={draft.placeholder ?? ''}
            maxLength={200}
            disabled={disabled}
            placeholder={t('placeholderPlaceholder')}
            onChange={(event) => onChange({ placeholder: event.target.value })}
          />
        </PresetFormItem>

        <PresetFormItem label={t('required')} fullWidth>
          <Switch
            checked={draft.required}
            disabled={disabled}
            onChange={(required) => onChange({ required })}
          />
        </PresetFormItem>
      </div>

      {isChoiceField(draft.type) && (
        <PresetFormItem label={t('options')} error={errors.options} required fullWidth>
          <Flex vertical gap={8}>
            {draft.options.map((option, index) => (
              <Flex key={`option-${index}`} gap={8} align="center">
                <Input
                  value={option}
                  maxLength={100}
                  disabled={disabled}
                  placeholder={t('optionPlaceholder', { number: index + 1 })}
                  onChange={(event) => onUpdateOption(index, event.target.value)}
                />
                <Button
                  danger
                  type="text"
                  htmlType="button"
                  icon={<Delete />}
                  aria-label={t('removeOption')}
                  disabled={disabled}
                  className={styles.iconButton}
                  onClick={() =>
                    onChange({
                      options: draft.options.filter((_, optionIndex) => optionIndex !== index),
                    })
                  }
                />
              </Flex>
            ))}
            <Button
              type="dashed"
              htmlType="button"
              icon={<PlusBlueWithoutBackground />}
              disabled={disabled || draft.options.length >= 20}
              className={styles.addButton}
              onClick={() => onChange({ options: [...draft.options, ''] })}
            >
              {t('addOption')}
            </Button>
          </Flex>
        </PresetFormItem>
      )}

      <Flex gap={8} justify="end" className={styles.additionalFieldEditorActions}>
        <Button htmlType="button" disabled={disabled} onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button type="primary" htmlType="button" disabled={disabled} onClick={onSave}>
          {t(isCreating ? 'add' : 'apply')}
        </Button>
      </Flex>
    </div>
  );
};
